/**
 * 지점 주소 표시 규칙 — **번역 주소 + 현지 원문 주소 병기** (10-07).
 *
 * 일본 지점 주소를 기계 번역하면 지명이 뜻으로 풀려 버린다
 * (東京都大田区蒲田3-15 → "도쿄도 대전구 가마타 3-15", ららぽーと海老名 → "라라포토 새우 이름").
 * 번역 주소는 별도 작업으로 원문 기준 재번역해 데이터를 고치되, 지도·택시에 넣을 값은
 * 원문이어야 하므로 일본 지점을 일본어 외 화면에서 볼 때는
 *   - 첫 줄: 화면 언어 번역 주소 + 번역 상세("주소, 상세" — 기존 표기)
 *   - 둘째 줄: 작은 "현지 주소" 라벨 + 일본어 원문 주소 + 원문 상세(branches.lang_code='ja' 행)
 *   - 복사: 원문(주소 + 상세)
 * 로 보여 준다.
 *
 * - 대상: 지점 국가 JP **그리고** 화면 언어 ≠ ja. 한국 지점·일본어 화면은 기존과 같다.
 * - 원문 행(ja address)이 없으면(데이터 누락) 기존 표시로 폴백한다.
 * - 번역 주소가 비어 있으면 원문을 첫 줄로(라벨 포함) 올린다.
 * - 주소 데이터 자체는 바꾸지 않는다 — 화면 규칙만.
 *
 * 화면(상품 상세 지도·모달, 지점 허브, 예약 상세·바우처)은 이 파일의 결과만 렌더하고,
 * 데이터 계층은 `needsLocalAddress` 로 원문 행을 더 읽을지 정한다(규칙의 단일 출처).
 */

/** 원문 주소를 보여 줄 국가 → 원문 언어. 지금은 일본만. */
const LOCAL_ADDRESS_LANG_BY_COUNTRY: Record<string, string> = {
  JP: "ja",
};

/** 원문 주소로 쓰는 언어 코드 전체 — 지점 목록을 일괄 조회할 때 받아 올 언어 행. */
export const LOCAL_ADDRESS_LANGS: readonly string[] = [
  ...new Set(Object.values(LOCAL_ADDRESS_LANG_BY_COUNTRY)),
];

function clean(value: string | null | undefined): string {
  return (value ?? "").trim();
}

/** 원문 행의 언어 코드(예: "ja"). 원문 표시 대상 국가가 아니면 null. */
export function localAddressLang(country: string | null | undefined): string | null {
  return LOCAL_ADDRESS_LANG_BY_COUNTRY[clean(country).toUpperCase()] ?? null;
}

/**
 * 이 화면 언어에서 이 국가 지점에 원문 주소를 보여 줘야 하는가.
 * 데이터 계층이 원문 행을 추가로 읽을지 판단할 때도 이 함수를 쓴다.
 */
export function needsLocalAddress(
  locale: string,
  country: string | null | undefined,
): boolean {
  const lang = localAddressLang(country);
  if (!lang) return false;
  return !locale.toLowerCase().startsWith(lang);
}

export interface BranchAddressDisplayInput {
  locale: string;
  /** 지점 국가(ISO 2자리, branches.country) */
  country: string | null | undefined;
  /** 원문(ja 행) 주소 */
  localAddress?: string | null;
  /** 원문(ja 행) 상세 주소 */
  localDetail?: string | null;
  /** 화면 언어 행 주소 */
  translatedAddress?: string | null;
  /** 화면 언어 행 상세 주소 */
  translatedDetail?: string | null;
}

export interface BranchAddressDisplay {
  /**
   * 첫 줄 — 번역 "주소, 상세"(기존 표기). 원문 대상인데 번역 주소가 비어 있으면
   * 원문 "주소 상세"(이때 `primaryIsLocal`).
   */
  primary: string;
  /** 첫 줄이 원문(번역 누락 폴백)인가 — 첫 줄 앞에 "현지 주소" 라벨 */
  primaryIsLocal: boolean;
  /**
   * 둘째 줄 — 원문 "주소 상세"(앞에 "현지 주소" 라벨). 원문 대상이고 첫 줄이 번역이며
   * 원문 주소가 번역 주소와 (공백 차이 외에) 다를 때만.
   */
  secondary: string | null;
  /** 복사 버튼이 복사할 문자열 — 원문 대상이면 원문 "주소 상세" */
  copyText: string;
  /** 원문(현지) 주소 대상인가 — "현지 주소 복사" 문구 분기용 */
  isLocal: boolean;
}

/** 주소와 상세 사이 — 한자·가나 표기 언어(ja·zh)는 공백, 그 밖은 ", ". */
export function addressDetailSeparator(locale: string): string {
  const lang = locale.toLowerCase().split("-")[0];
  return lang === "ja" || lang === "zh" ? " " : ", ";
}

/** 공백 차이("蒲田 3-15" vs "蒲田3-15")는 같은 주소로 본다. */
function sameAddress(a: string, b: string): boolean {
  return a.replace(/\s+/g, "") === b.replace(/\s+/g, "");
}

export function getBranchAddressDisplay(
  input: BranchAddressDisplayInput,
): BranchAddressDisplay {
  const translated = clean(input.translatedAddress);
  const translatedDetail = clean(input.translatedDetail);
  const local = clean(input.localAddress);

  // 기존 표기 — 표시는 "주소, 상세", 복사는 "주소 상세". 일본어·중국어 화면은 라틴 쉼표가
  // 어색하다("新宿3-37, エコステーション…", 10-15) — 원문 줄과 같은 공백 구분.
  const translatedLine = translatedDetail
    ? `${translated}${addressDetailSeparator(input.locale)}${translatedDetail}`
    : translated;

  if (local && needsLocalAddress(input.locale, input.country)) {
    const localDetail = clean(input.localDetail);
    const localLine = localDetail ? `${local} ${localDetail}` : local;
    if (!translated) {
      // 번역 주소 누락 — 원문을 첫 줄로(라벨 포함).
      return {
        primary: localLine,
        primaryIsLocal: true,
        secondary: null,
        copyText: localLine,
        isLocal: true,
      };
    }
    return {
      primary: translatedLine,
      primaryIsLocal: false,
      secondary: sameAddress(translated, local) ? null : localLine,
      copyText: localLine,
      isLocal: true,
    };
  }

  return {
    primary: translatedLine,
    primaryIsLocal: false,
    secondary: null,
    copyText: translatedDetail ? `${translated} ${translatedDetail}` : translated,
    isLocal: false,
  };
}

/**
 * 지점 허브 행·시트 머리의 부제(한 줄 말줄임이라 원문은 넣지 않는다).
 * 원문 대상이면 "가는 길 · 번역 주소"(상세 제외, 번역이 비면 원문 주소),
 * 아니면 기존대로 "가는 길"(없으면 주소).
 */
export function getStationSubtitle(input: {
  locale: string;
  country: string | null | undefined;
  directions?: string | null;
  localAddress?: string | null;
  translatedAddress?: string | null;
}): string {
  const directions = clean(input.directions);
  const display = getBranchAddressDisplay({
    locale: input.locale,
    country: input.country,
    localAddress: input.localAddress,
    translatedAddress: input.translatedAddress,
  });
  if (display.isLocal) {
    // primary = 번역 주소(상세 없이 넘겼으므로), 번역이 비면 원문 주소.
    return directions ? `${directions} · ${display.primary}` : display.primary;
  }
  return directions || clean(input.translatedAddress);
}

/** 지점 행 모양(snake_case: 상품 상세 `Branch`, 예약 `pickup_branch`)에서 바로 계산. */
export function getBranchRowAddressDisplay(
  branch: {
    country?: string | null;
    address?: string | null;
    address_detail?: string | null;
    address_local?: string | null;
    address_detail_local?: string | null;
  },
  locale: string,
): BranchAddressDisplay {
  return getBranchAddressDisplay({
    locale,
    country: branch.country,
    localAddress: branch.address_local,
    localDetail: branch.address_detail_local,
    translatedAddress: branch.address,
    translatedDetail: branch.address_detail,
  });
}
